import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { CaretUpDownIcon, CheckIcon } from '@phosphor-icons/react';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';

import { Button, Text } from '@components';
import { VariantType } from '@enums';
import { cn, colors, validateValueEqual } from '@utils';

import { Option, useSelectBox } from './useSelectBox';

interface SelectBoxProps {
  options: Option[];
  name: string;
  onOptionSelect?: (option: Option) => void;
  color?: any;
  customMenu?: ReactNode;
  menuItemClassName?: string;
  className?: string;
}

export const SelectBox: FC<SelectBoxProps> = ({
  options,
  name,
  onOptionSelect,
  color = 'bg-slate-700 bg-opacity-5',
  customMenu,
  menuItemClassName = '',
  className,
}) => {
  const { field, meta, selected, setValue } = useSelectBox({ name, options });
  const [menuWidth, setMenuWidth] = useState<number>(0);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (buttonRef.current) {
      setMenuWidth(buttonRef.current.offsetWidth * 3);
    }
  }, [buttonRef]);

  const handleOptionClick = (option: Option) => {
    setValue(option.value);
    if (onOptionSelect) {
      onOptionSelect(option);
    }
  };

  const dynamicContainerClasses = cn(
    'inline-flex justify-between w-full gap-2 rounded-md text-sm/6 shadow-inner shadow-white/10 focus:outline-none enabled:shadow-none',
    colors[color] ?? color,
    customMenu ? 'px-2.5 py-2 sm:py-3 sm:px-3.5' : 'sm:py-3 sm:px-3.5 p-2.5',
    className,
  );

  if (!name || !selected) return null;

  return (
    <div className="relative">
      <Menu>
        <div ref={buttonRef}>
          <MenuButton as={'div'}>
            <Button className={dynamicContainerClasses}>
              {customMenu ?? (
                <>
                  <Text $level={6} color="text-gray-600" className="truncate">
                    {selected?.label}
                  </Text>
                  <CaretUpDownIcon className="size-4 sm:size-5 text-gray-400 flex-shrink-0" />
                </>
              )}
            </Button>
          </MenuButton>
        </div>

        <div className="relative">
          <MenuItems
            className="absolute right-0 z-50 origin-top-right mt-2.5 mb-1.5 bg-white shadow-card rounded-xl max-w-32 sm:max-w-48"
            style={{ width: `${menuWidth}px` }}
          >
            <div className="max-h-40 flex flex-col p-1.5 gap-1.5 overflow-y-auto padded-scrollbar-track">
              {options.map((option, index) => {
                const isCurrent = validateValueEqual(option.value.toString(), field.value.toString());

                const dynamicRowClasses = cn(
                  'group w-full rounded-lg justify-between relative enabled:shadow-none',
                  'pl-3 pr-8 sm:pr-10 py-2.5',
                  isCurrent
                    ? 'bg-slate-100 text-slate-900 hover:cursor-not-allowed'
                    : 'text-gray-900 hover:text-green-900 hover:bg-gray-100',
                  menuItemClassName,
                );

                return (
                  <MenuItem key={index}>
                    <Button
                      variant={VariantType.Clean}
                      disabled={isCurrent}
                      className={dynamicRowClasses}
                      onClick={() => handleOptionClick(option)}
                      hintPlacement="left"
                      hint={
                        <Text $level={5} $customizeColor>
                          {option.label}
                        </Text>
                      }
                      adornment={
                        isCurrent && (
                          <CheckIcon
                            weight="bold"
                            className="size-3 sm:size-4 absolute right-3 flex-shrink-0 text-gray-400"
                          />
                        )
                      }
                    >
                      <Text $level={6} className="truncate">
                        {option.label}
                      </Text>
                    </Button>
                  </MenuItem>
                );
              })}
            </div>
          </MenuItems>
        </div>
      </Menu>
      {meta.touched && meta.error && <div className="text-red-500 text-xs mt-1">{meta.error}</div>}
    </div>
  );
};
